import React from 'react'
import { useParams } from 'react-router-dom';
import { GET } from '../frameworks/HttpClient';
import { URL_REPORT } from '../constants/urls';
import HeaderScreen from '../common/HeaderScreen';
import { SECONDARY_COLOR, WHITE_COLOR } from '../constants/color';
import { Layout, Descriptions, Spin, Flex, Typography, Table, Button } from 'antd';
import ErrorMessages from '../common/ErrorMessages';
import ReportButton from "../components/common/ReportButton";
import { dateTimeToHumanString, dateToHumanString, formatComma } from '../frameworks/Util';
import { POND_STATUS_TH, TANK_STATUS_TH } from '../constants/string';
import { FilePdfOutlined } from '@ant-design/icons';
import { PATH } from '../routes/CustomRoutes';
import _ from 'lodash';

export default function TankSummaryScreen() {
  const { id } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [errorMessages, setErrorMessages] = React.useState(null);
  const [data, setData] = React.useState(null); 

  React.useEffect(() => {
    if(id){
      fetchData();
    }
  },[id])

  const fetchData = async() => {
    setLoading(true);
    setErrorMessages(null);
    try {
      const response = await GET(`${URL_REPORT.TANK_SUMMARY}${id}/`);
      setData(response.data);
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setLoading(false);
    }
  }

    return (
    <Layout>
      <HeaderScreen 
        title="รายละเอียดสต๊อกน้ำยางข้น" 
        style={{ backgroundColor : SECONDARY_COLOR, 
        color : WHITE_COLOR, fontSize : 18, fontWeight: 'bolder' }}
        onClick={() => window.close()}
      />  
      <Layout.Content
        style={{ margin: 24, minHeight: 280, padding: "0px 24px", }}
      >
        <Spin spinning={loading}>
          <ErrorMessages message={errorMessages}/>
          <Descriptions
            size='small'
            title={
              <Flex justify="space-between">
                ข้อมูลเบื้องต้น
                <ReportButton url={`${URL_REPORT.CONCENTRATE_LATEX_SUMMARY}${id}/`}>พิมพ์รายงาน</ReportButton>
              </Flex>
            }
            bordered
            column={1}
            items={[
              { label: 'หมายเลขแทงก์', children: data?.tank_name },
              { label: 'ล็อต', children: data?.lot_no },
              { label: 'วันที่เริ่มผลิต', children: dateTimeToHumanString(data?.created) },
              { label: 'วันที่สิ้นสุดการผลิต', children: data?.close_datetime ? dateTimeToHumanString(data?.close_datetime) : '-' },
              { label: 'น้ำหนักที่ต้องการ', children: formatComma(data?.require_weight) },
              { label: 'ค่าที่วัดได้', children: formatComma(data?.current_height) },
              { label: 'น้ำหนักที่ผลิตได้', children: formatComma(data?.produce_weight) },
              { label: 'สถานะ', children: TANK_STATUS_TH[data?.state] },
              { label: 'ผู้ทำรายการเริ่มต้น', children: data?.created_by_name },
            ]}/>
          <Typography.Title level={5}>ผลิตจากล็อตน้ำยางข้นนำเข้า</Typography.Title>
          <Table
            size='small'
            bordered
            pagination={false}
            columns={[
              { title: 'ล็อต', dataIndex: 'source_lot_no' },
              { title: 'บริษัทผู้นำเข้า', dataIndex: 'supplier_name' },
              { title: 'ประเภท', dataIndex: 'product_name' },
              { title: 'ผู้นำเข้า', dataIndex: 'created_by_name' },
              { title: 'ปริมาณที่ผลิต', dataIndex: 'weight', render: (value) => formatComma(value) },
              { title: 'วันที่นำเข้า', dataIndex: 'created', render: (value) => dateTimeToHumanString(value) },
            ]}
            dataSource={data?.concentrate_import_transactions}
            summary={pageData => {
              let total_weight = 0;
              pageData.forEach(({ weight }) => {
                total_weight += Number(weight);
              });
              return (
                <>{_.get(data, 'concentrate_import_transactions.length', 0) > 0 &&
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}/>
                    <Table.Summary.Cell index={2}/>
                    <Table.Summary.Cell index={3}/>
                    <Table.Summary.Cell index={4}>{formatComma(total_weight)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}/>
                  </Table.Summary.Row>}
                </>
              );
            }}/>
          <Typography.Title level={5}>ผลิตจากล็อตน้ำยางสด</Typography.Title>
          <Table
            size='small'
            bordered
            pagination={false}
            columns={[
              { title: 'ล็อต', dataIndex: 'lot_no', render: (value, original) => <span>{value} <Button type="link" icon={<FilePdfOutlined />} onClick={() => window.open(`${window.origin}${PATH.POND_SUMMARY}/${original.id}`)} /></span> },
              { title: 'บ่อ', dataIndex: 'pond_name' },
              { title: 'ค่าที่วัดได้', dataIndex: 'current_height', render: (value) => formatComma(value) },
              { title: 'ปริมาณสั่ง', dataIndex: 'raw_latex_weight', render: (value) => formatComma(value) },
              { title: 'สถานะล็อต', dataIndex: 'state', render: (value) => POND_STATUS_TH[value] },
            ]}
            dataSource={data?.pond_transactions}
            summary={pageData => {
              let total_raw_latex_weight = 0;
              pageData.forEach(({ raw_latex_weight }) => {
                total_raw_latex_weight += Number(raw_latex_weight);
              });
              return (
                <>{_.get(data, 'pond_transactions.length', 0) > 0 &&
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}/>
                    <Table.Summary.Cell index={2}/>
                    <Table.Summary.Cell index={3}>{formatComma(total_raw_latex_weight)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}/>
                  </Table.Summary.Row>}
                </>
              );
            }}/>
          <Typography.Title level={5}>ผลิตจากล็อตน้ำยางข้น</Typography.Title>
          <Table
            size='small'
            bordered
            pagination={false}
            columns={[
              { title: 'ล็อต', dataIndex: 'lot_no', render: (value, original) => <span>{value} <Button type="link" icon={<FilePdfOutlined />} onClick={() => window.open(`${window.origin}${PATH.TANK_SUMMARY}/${original.id}`)} /></span> },
              { title: 'แทงก์', dataIndex: 'tank_name' },
              { title: 'ค่าที่วัดได้', dataIndex: 'current_height', render: (value) => formatComma(value) },
              { title: 'ปริมาณสั่ง', dataIndex: 'weight', render: (value) => formatComma(value) },
              { title: 'ปริมาณที่ผลิต', dataIndex: 'current_weight', render: (value) => formatComma(value) },
              { title: 'สถานะล็อต', dataIndex: 'state', render: (value) => TANK_STATUS_TH[value] },
            ]}
            dataSource={data?.transfer_in_transactions}
            summary={pageData => {
              let total_weight = 0;
              let total_current_weight = 0;
              pageData.forEach(({ weight, current_weight }) => {
                total_weight += Number(weight);
                total_current_weight += Number(current_weight);
              });
              return (
                <>{_.get(data, 'transfer_in_transactions.length', 0) > 0 &&
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}/>
                    <Table.Summary.Cell index={2}/>
                    <Table.Summary.Cell index={3}>{formatComma(total_weight)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{formatComma(total_current_weight)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}/>
                  </Table.Summary.Row>}
                </>
              );
            }}/>
          <Typography.Title level={5}>รายการใช้สารเคมีคุณภาพ</Typography.Title>
          <Table
            size='small'
            bordered
            pagination={false}
            columns={[
              { title: 'ล็อตเคมีคุณภาพ', dataIndex: 'lot_no', render: (value, original) => <span> {value} {value && <Button type="link" icon={<FilePdfOutlined />} onClick={() => window.open(`${window.origin}${PATH.QUALITY_CHEMICAL_SUMMARY}/${original.stock_ref}`)} />} </span> },
              { title: 'วันที่ใช้งาน', dataIndex: 'order_date', render: (value) => dateTimeToHumanString(value) },
              { title: 'ปริมาณสั่ง', dataIndex: 'amount', render: (value) => formatComma(value) },
              { title: 'ผู้สั่งงาน', dataIndex: 'lab_user' },
              { title: 'ผู้รับงาน', dataIndex: 'input_user' },
            ]}
            dataSource={data?.lab_inputs}
            summary={pageData => {
              let total_amount = 0;
              pageData.forEach(({ amount }) => {
                total_amount += Number(amount);
              });
              return (
                <>{_.get(data, 'lab_inputs.length', 0) > 0 &&
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}/>
                    <Table.Summary.Cell index={2}>{formatComma(total_amount)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3}/>
                    <Table.Summary.Cell index={4}/>
                  </Table.Summary.Row>}
                </>
              );
            }}/>
          <Typography.Title level={5}>รายการขายน้ำยางข้น</Typography.Title>
          <Table
            size='small'
            bordered
            pagination={false}
            columns={[
              { title: 'ชื่อลูกค้า', dataIndex: 'buyer_name' },
              { title: 'วันที่ส่งออก', dataIndex: 'do_date', render: (value) => dateToHumanString(value) },
              { title: 'SERIAL ID', dataIndex: 'serial_id' },
              { title: 'SERIAL NO', dataIndex: 'serial_no' },
              { title: 'ปริมาณสั่งซื้อ', dataIndex: 'weight', render: (value) => formatComma(value) },
            ]}
            dataSource={data?.load_transactions}
            summary={pageData => {
              let total_weight = 0;
              pageData.forEach(({ weight }) => {
                total_weight += Number(weight);
              });
              return (
                <>{_.get(data, 'load_transactions.length', 0) > 0 &&
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}/>
                    <Table.Summary.Cell index={2}/>
                    <Table.Summary.Cell index={3}/>
                    <Table.Summary.Cell index={4}>{formatComma(total_weight)}</Table.Summary.Cell>
                  </Table.Summary.Row>}
                </>
              );
            }}/>
          <Typography.Title level={5}>รายการย้ายล็อตน้ำยางข้น</Typography.Title>
          <Table
            size='small'
            bordered
            pagination={false}
            columns={[  
              { title: 'ล็อต', dataIndex: 'lot_no', render: (value, original) => <span>{value} <Button type="link" icon={<FilePdfOutlined />} onClick={() => window.open(`${window.origin}${PATH.TANK_SUMMARY}/${original.id}`)} /></span> },
              { title: 'แทงก์', dataIndex: 'tank_name' },
              { title: 'ค่าที่วัดได้', dataIndex: 'current_height', render: (value) => formatComma(value) },
              { title: 'ปริมาณสั่ง', dataIndex: 'weight', render: (value) => formatComma(value) },
              { title: 'ปริมาณที่ผลิต', dataIndex: 'current_weight', render: (value) => formatComma(value) },
              { title: 'สถานะล็อต', dataIndex: 'state', render: (value) => TANK_STATUS_TH[value] },
            ]}
            dataSource={data?.transfer_out_transactions}
            summary={pageData => {
              let total_weight = 0;
              let total_current_weight = 0;
              pageData.forEach(({ weight, current_weight }) => {
                total_weight += Number(weight);
                total_current_weight += Number(current_weight);
              });
              return (
                <>{_.get(data, 'transfer_out_transactions.length', 0) > 0 &&
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}/>
                    <Table.Summary.Cell index={2}/>
                    <Table.Summary.Cell index={3}>{formatComma(total_weight)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{formatComma(total_current_weight)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}/>
                  </Table.Summary.Row>}
                </>
              );
            }}/>
        </Spin>
      </Layout.Content>
    </Layout>
  )
}